import React, { useMemo, useState } from 'react';
import HamburgerMenu from './HamburgerMenu';
import api from './api';

const SUGGESTED_PROMPTS = [
    'Build a leadership-ready summary of readiness trends from this month.',
    'Compare PMCS and NMCS trends and identify likely drivers.',
    'Create an argument-focused write-up with citations for overdue inspection risks.',
    'Summarize maintenance bottlenecks and recommend immediate actions.',
];

const CAPABILITIES = [
    { title: 'Policy + Contract Q&A', text: 'Answers from indexed pubs and contract sources with citations.' },
    { title: 'Analytical Reporting', text: 'Can produce write-ups and build structured document outputs.' },
    { title: 'Export Formats', text: 'Generate Word, Excel, PDF, and PowerPoint report outputs.' },
    { title: 'Secure Routing', text: 'Uses the authenticated METS backend proxy without exposing service credentials.' },
];

const KPI_PROFILES = [
    'general',
    'readiness',
    'maintenance',
    'supply',
    'flight_ops',
    'safety',
    'fiscal',
    'work_order',
];

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    const colors = {
        pageBg: isDark ? '#121212' : '#f5f7fb',
        text: isDark ? '#ececec' : '#1f2937',
        surface: isDark ? '#1e1e1e' : '#ffffff',
        card: isDark ? '#252525' : '#ffffff',
        border: isDark ? '#3f3f3f' : '#d7dce5',
        muted: isDark ? '#b3b9c4' : '#5f6b7a',
        primary: isDark ? '#3aa0ff' : '#1769e0',
        primarySoft: isDark ? '#173a5d' : '#e8f1ff',
        inputBg: isDark ? '#171717' : '#ffffff',
        errorBg: isDark ? '#3a1f1f' : '#fde8e8',
        errorText: isDark ? '#ffb3b3' : '#9f1d1d',
        successBg: isDark ? '#1f3a28' : '#e9f9ee',
        successText: isDark ? '#9fe6b5' : '#1f7a3d',
    };

    return {
        page: {
            minHeight: '100vh',
            backgroundColor: colors.pageBg,
            color: colors.text,
            padding: '20px 0 30px',
        },
        container: {
            width: '92%',
            maxWidth: '1450px',
            margin: '0 auto',
            fontFamily: 'Inter, system-ui, sans-serif',
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '22px',
        },
        titleWrap: {
            flexGrow: 1,
            textAlign: 'center',
        },
        title: {
            margin: 0,
            fontSize: '1.8rem',
            letterSpacing: '0.2px',
        },
        subtitle: {
            margin: '4px 0 0',
            color: colors.muted,
            fontSize: '0.95rem',
        },
        themeToggleButton: {
            padding: '8px 12px',
            border: `1px solid ${colors.border}`,
            borderRadius: '8px',
            backgroundColor: colors.surface,
            color: colors.text,
            cursor: 'pointer',
        },
        capabilitiesGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
            gap: '12px',
            marginBottom: '18px',
        },
        capabilityCard: {
            backgroundColor: colors.card,
            border: `1px solid ${colors.border}`,
            borderRadius: '12px',
            padding: '12px',
        },
        capabilityTitle: {
            margin: '0 0 6px',
            fontSize: '0.95rem',
        },
        capabilityText: {
            margin: 0,
            color: colors.muted,
            fontSize: '0.88rem',
            lineHeight: 1.45,
        },
        workspace: {
            display: 'grid',
            gridTemplateColumns: 'minmax(330px, 430px) minmax(460px, 1fr)',
            gap: '14px',
        },
        panel: {
            backgroundColor: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: '14px',
            padding: '16px',
        },
        panelTitle: {
            margin: '0 0 8px',
            fontSize: '1.03rem',
        },
        muted: {
            margin: 0,
            color: colors.muted,
            fontSize: '0.9rem',
            lineHeight: 1.45,
        },
        suggestionsWrap: {
            margin: '12px 0 0',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
        },
        suggestionBtn: {
            textAlign: 'left',
            border: `1px solid ${colors.border}`,
            borderRadius: '10px',
            padding: '9px 11px',
            backgroundColor: colors.inputBg,
            color: colors.text,
            cursor: 'pointer',
            fontSize: '0.86rem',
        },
        row: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '12px',
            flexWrap: 'wrap',
        },
        label: {
            fontWeight: 600,
            minWidth: '86px',
            fontSize: '0.92rem',
        },
        select: {
            padding: '8px 10px',
            borderRadius: '8px',
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.inputBg,
            color: colors.text,
            minWidth: '180px',
        },
        textInput: {
            padding: '8px 10px',
            borderRadius: '8px',
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.inputBg,
            color: colors.text,
            minWidth: '180px',
        },
        checkbox: {
            transform: 'translateY(1px)',
        },
        textarea: {
            width: '100%',
            minHeight: '180px',
            padding: '12px',
            borderRadius: '10px',
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.inputBg,
            color: colors.text,
            boxSizing: 'border-box',
            resize: 'vertical',
            lineHeight: 1.5,
            fontSize: '0.95rem',
        },
        actions: {
            marginTop: '12px',
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap',
        },
        askBtn: {
            border: 'none',
            borderRadius: '9px',
            padding: '11px 16px',
            fontWeight: 700,
            fontSize: '0.93rem',
            cursor: 'pointer',
            color: '#fff',
            backgroundColor: colors.primary,
        },
        secondaryBtn: {
            borderRadius: '9px',
            padding: '11px 14px',
            fontWeight: 600,
            cursor: 'pointer',
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.inputBg,
            color: colors.text,
        },
        disabled: {
            opacity: 0.6,
            cursor: 'not-allowed',
        },
        error: {
            marginTop: '12px',
            borderRadius: '10px',
            padding: '10px 12px',
            backgroundColor: colors.errorBg,
            color: colors.errorText,
            border: `1px solid ${colors.errorText}33`,
            fontSize: '0.9rem',
        },
        statusBar: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '8px',
            flexWrap: 'wrap',
            marginBottom: '10px',
        },
        badge: {
            display: 'inline-block',
            borderRadius: '999px',
            padding: '4px 10px',
            fontSize: '0.76rem',
            fontWeight: 700,
            backgroundColor: colors.primarySoft,
            color: colors.primary,
        },
        answerWrap: {
            border: `1px solid ${colors.border}`,
            borderRadius: '10px',
            backgroundColor: colors.inputBg,
            padding: '12px',
            minHeight: '430px',
        },
        answer: {
            margin: 0,
            whiteSpace: 'pre-wrap',
            lineHeight: 1.55,
            fontSize: '0.94rem',
        },
        successBanner: {
            borderRadius: '9px',
            padding: '8px 11px',
            marginBottom: '10px',
            backgroundColor: colors.successBg,
            color: colors.successText,
            fontSize: '0.85rem',
        },
        citationsTitle: {
            marginTop: '12px',
            fontSize: '0.88rem',
            color: colors.muted,
            fontWeight: 600,
        },
        citationsList: {
            marginTop: '6px',
            paddingLeft: '20px',
            color: colors.muted,
            fontSize: '0.88rem',
        },
    };
};

function AIPage({ theme, toggleTheme }) {
    const styles = getStyles(theme);
    const [question, setQuestion] = useState('');
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [focusFolder, setFocusFolder] = useState('all');
    const [analyticalMode, setAnalyticalMode] = useState(true);
    const [kpiProfile, setKpiProfile] = useState('general');
    const [requestPath, setRequestPath] = useState('Not started');

    const answerWordCount = useMemo(() => {
        const text = result?.answer || '';
        return text ? text.trim().split(/\s+/).length : 0;
    }, [result]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        const trimmedQuestion = question.trim();
        if (!trimmedQuestion) {
            setError('Enter a question for the AI assistant.');
            return;
        }

        setIsLoading(true);
        setError('');
        setResult(null);
        setRequestPath('Running...');

        try {
            const response = await api.post('/t6ai/chat/', {
                question: trimmedQuestion,
                focus_folder: focusFolder || 'all',
                analytical_mode: analyticalMode,
                kpi_profile: kpiProfile,
            });
            setRequestPath('METS secure proxy (/t6ai/chat/)');
            setResult(response.data);
        } catch (err) {
            const detail = err.response?.data?.detail || err.response?.data?.error || err.message;
            setError(`Unable to get an answer from AI service. ${detail || ''}`.trim());
            setRequestPath('Failed');
        } finally {
            setIsLoading(false);
        }
    };

    const renderCitation = (citation, index) => {
        if (typeof citation === 'string') {
            return <li key={index}>{citation}</li>;
        }
        return (
            <li key={index}>
                {citation.title || citation.source || citation.url || JSON.stringify(citation)}
            </li>
        );
    };

    return (
        <div style={styles.page}>
            <div style={styles.container}>
                <div style={styles.header}>
                    <HamburgerMenu theme={theme} />
                    <div style={styles.titleWrap}>
                        <h1 style={styles.title}>AI Assistant Workspace</h1>
                        <p style={styles.subtitle}>Evidence-based Q&A, reporting support, and export-ready analysis</p>
                    </div>
                    <button onClick={toggleTheme} style={styles.themeToggleButton}>
                        {theme === 'light' ? 'Dark' : 'Light'}
                    </button>
                    <div style={{ width: '42px' }} />
                </div>

                <div style={styles.capabilitiesGrid}>
                    {CAPABILITIES.map((item) => (
                        <div key={item.title} style={styles.capabilityCard}>
                            <h3 style={styles.capabilityTitle}>{item.title}</h3>
                            <p style={styles.capabilityText}>{item.text}</p>
                        </div>
                    ))}
                </div>

                <div style={styles.workspace}>
                    <div style={styles.panel}>
                        <h2 style={styles.panelTitle}>Ask</h2>
                        <p style={styles.muted}>
                            Requests go through the METS backend so the AI service credential is never exposed in the browser.
                        </p>

                        <div style={styles.suggestionsWrap}>
                            {SUGGESTED_PROMPTS.map((prompt) => (
                                <button
                                    key={prompt}
                                    type="button"
                                    style={styles.suggestionBtn}
                                    onClick={() => setQuestion(prompt)}
                                    disabled={isLoading}
                                >
                                    {prompt}
                                </button>
                            ))}
                        </div>

                        <form onSubmit={handleSubmit} style={{ marginTop: '14px' }}>
                            <div style={styles.row}>
                                <label htmlFor="focus-folder" style={styles.label}>Focus folder</label>
                                <input
                                    id="focus-folder"
                                    type="text"
                                    value={focusFolder}
                                    onChange={(event) => setFocusFolder(event.target.value)}
                                    style={styles.textInput}
                                    disabled={isLoading}
                                    placeholder="all"
                                />
                            </div>
                            <div style={styles.row}>
                                <label htmlFor="kpi-profile" style={styles.label}>KPI profile</label>
                                <select
                                    id="kpi-profile"
                                    value={kpiProfile}
                                    onChange={(event) => setKpiProfile(event.target.value)}
                                    style={styles.select}
                                    disabled={isLoading}
                                >
                                    {KPI_PROFILES.map((profile) => (
                                        <option value={profile} key={profile}>
                                            {profile.replace('_', ' ')}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div style={styles.row}>
                                <label htmlFor="analytical-mode" style={styles.label}>Analytical</label>
                                <label htmlFor="analytical-mode" style={styles.muted}>
                                    <input
                                        id="analytical-mode"
                                        type="checkbox"
                                        checked={analyticalMode}
                                        onChange={(event) => setAnalyticalMode(event.target.checked)}
                                        style={styles.checkbox}
                                        disabled={isLoading}
                                    />{' '}
                                    Enable KPI-focused trend/stat analysis
                                </label>
                            </div>

                            <textarea
                                value={question}
                                onChange={(event) => setQuestion(event.target.value)}
                                placeholder="Example: Build a concise readiness argument using this week’s NMCS and PMCS data."
                                style={styles.textarea}
                                disabled={isLoading}
                            />

                            <div style={styles.actions}>
                                <button
                                    type="submit"
                                    style={{ ...styles.askBtn, ...(isLoading ? styles.disabled : {}) }}
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Analyzing...' : 'Ask AI'}
                                </button>
                                <button
                                    type="button"
                                    style={styles.secondaryBtn}
                                    disabled={isLoading}
                                    onClick={() => {
                                        setQuestion('');
                                        setResult(null);
                                        setError('');
                                        setRequestPath('Not started');
                                        setFocusFolder('all');
                                        setAnalyticalMode(true);
                                        setKpiProfile('general');
                                    }}
                                >
                                    Clear
                                </button>
                            </div>
                        </form>

                        {error && <div style={styles.error}>{error}</div>}
                    </div>

                    <div style={styles.panel}>
                        <div style={styles.statusBar}>
                            <h2 style={{ margin: 0 }}>Response</h2>
                            <span style={styles.badge}>{requestPath}</span>
                        </div>

                        {result && (
                            <div style={styles.successBanner}>
                                Completed · {answerWordCount} words · KPI: {kpiProfile} · Analytical: {analyticalMode ? 'On' : 'Off'}
                            </div>
                        )}

                        <div style={styles.answerWrap}>
                            {!result && !isLoading && (
                                <p style={styles.muted}>
                                    Ask a question to generate a grounded answer with optional citations.
                                </p>
                            )}
                            {isLoading && (
                                <p style={styles.muted}>Working on your request...</p>
                            )}
                            {result && (
                                <>
                                    <p style={styles.answer}>{result.answer || JSON.stringify(result, null, 2)}</p>
                                    {result.generation_mode && (
                                        <div style={styles.citationsTitle}>Generation mode: {result.generation_mode}</div>
                                    )}
                                    {Array.isArray(result.citations) && result.citations.length > 0 && (
                                        <>
                                            <div style={styles.citationsTitle}>Citations</div>
                                            <ul style={styles.citationsList}>
                                                {result.citations.map(renderCitation)}
                                            </ul>
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AIPage;
